import { appAjax, baseUrl } from "../lib/helper"
import { getToken } from "../providers/implicitGrantProvider"
import { IBrokerAPICallBody } from "../types/shared"

export type API_METHODS = "GET" | "PATCH" | "POST" | "DELETE" | "PUT"

const ensureStartsWithSlash = (text: string): string => {
	return text.startsWith("/") ? text : `/${text}`
}

export interface CRMAPIResponse {
	res: any
	status: string
	xhr: any
}

export const makeCRMApiCallWithDetails = async (path: string, method?: API_METHODS, body?: BodyInit): Promise<any> => {
	let status: string, xhr: any, result: any

	await appAjax({
		type: method || "GET",
		url: `/_api/${path}`,
		data: body,
		contentType: "application/json",
		headers: {
			Prefer: 'return=representation,odata.include-annotations="*"'
		},
		success: (res: any, resStatus: string, resXHR: any) => {
			status = resStatus
			xhr = resXHR
			result = res
		}
	})

	return [result, status, xhr]
}

export const makeCRMApiCall = async (path: string, method?: API_METHODS, body?: BodyInit, customApiPath?: string, parseHeaders?: string[]): Promise<any> => {
	let retVal: any

	await appAjax({
		type: method || "GET",
		url: `${customApiPath ? `/${customApiPath}/` : "/_api/"}${path}`,
		data: body,
		contentType: "application/json",
		headers: { Prefer: 'return=representation,odata.include-annotations="*"' },
		success: (res: any, resStatus: string, resXHR: any) => {
			if (parseHeaders) {
				retVal = parseHeaders.reduce((acc, header) => {
					const headerValue = resXHR.getResponseHeader(header)
					return { ...acc, [header]: headerValue }
				}, {})
			} else retVal = res
		}
	})

	return retVal
}

export const makeACMGCRMApiCall = async (path: string): Promise<any> => {
	const res = await fetch(`/_acmgapi/${path}`, { method: "Get" })
	return await res.json()
}

export const makeBrokerAPICall = async (accountId: string, path: string, method: API_METHODS, body?: Record<string, any>): Promise<any> => {
	const apiBody: IBrokerAPICallBody = {
		body: body,
		path: path,
		method: method
	}

	const result = await makeApiCall(`/makeBrokerAPICall?groupId=${accountId}`, "POST", JSON.stringify(apiBody), undefined)
	return result
}

export const makeApiCall = async (
	path: string,
	method?: API_METHODS,
	body?: BodyInit,
	customApiPath?: string,
	additionalHeaders?: HeadersInit,
	noHeader?: boolean,
	signal?: AbortSignal,
	mode?: RequestMode,
	customBaseUrl?: string
): Promise<any> => {
	const _apiPrefix = customApiPath ? `/${customApiPath}/` : "/_api"
	path = ensureStartsWithSlash(path)
	let apiPath = `${baseUrl}${_apiPrefix}${path}`
	if (customBaseUrl) {
		apiPath = `${customBaseUrl}${_apiPrefix}${path}`
	}
	let reqHeaders: HeadersInit | undefined = noHeader ? {} : { "Content-Type": "application/json", Accept: "application/json" }

	if (!customApiPath) {
		const authToken = await getToken()
		reqHeaders.Authorization = `Bearer ${authToken}`
	}
	reqHeaders = { ...reqHeaders, ...additionalHeaders }
	try {
		const init: RequestInit = { method: method || "GET", headers: reqHeaders, credentials: "include", body, signal }
		if (mode) init.mode = mode
		const response = await fetch(apiPath, init)
		if (!response.ok) {
			if (response.status === 401) {
				throw new Error("Unauthorized")
			} else {
				if (response.headers.get("content-type").toLowerCase().startsWith("application/json")) {
					const jsonResponse = await response.json()
					throw new Error(jsonResponse.error || jsonResponse.message || "Unknown error")
				} else {
					try {
						const errorText = await response.text()
						throw new Error(errorText)
					} catch (error) {
						const json = await response.json()
						throw json
					}
				}
			}
		} else {
			if (response.headers.get("content-type") === "application/octet-stream") {
				return await response.blob()
			} else if (response.statusText.toLowerCase() === "no content") {
				return {}
			} else {
				const jsonResponse = await response.json()
				if (jsonResponse.redirectTo) {
					window.location.assign(jsonResponse.redirectTo)
				} else {
					return jsonResponse
				}
			}
		}
	} catch (error) {
		if (error.name !== "AbortError") throw new Error(error)
		else console.error("Request aborted")
	}
}
